/**
 * Ako streaming event vocabulary (client side). Contract: docs/ai-tutor/streaming-events.md
 *
 * Wire frames are `data: {"type": "...", "data": {...}}` (optionally preceded by
 * an `event: <type>` line and an `id:` line). The legacy teaching-agent names
 * `content` and `end` remain the canonical wire names for compatibility; the
 * normalised names `delta` and `done` are accepted as aliases on input.
 */

export type TutorPhase = 'thinking' | 'tool' | 'writing'

export interface TutorStreamSource {
  id?: string
  title: string
  url?: string
  kind?: string
}

export type TutorStreamEvent =
  | { kind: 'start'; sessionId?: string }
  | { kind: 'status'; phase: TutorPhase; tool?: string; label?: string }
  | { kind: 'tool'; name: string; label?: string }
  | { kind: 'reasoning'; text: string }
  | { kind: 'delta'; text: string }
  | { kind: 'sources'; sources: TutorStreamSource[] }
  | {
      kind: 'done'
      messageId?: string
      sources?: TutorStreamSource[]
      stopReason?: string
      /** tutor-agent only: the turn was Stopped (learner or another tab). Absent for teaching-agent. */
      stopped?: boolean
      /** tutor-agent only: server turn id (also on `start`). Absent for teaching-agent. */
      turnId?: string
    }
  | { kind: 'error'; code?: string; message: string }
  /** Rich teaching events (video, book_excerpt, question, celebration, tool_call) pass through untouched. */
  | { kind: 'passthrough'; type: string; data: unknown }

const PHASES: ReadonlySet<string> = new Set(['thinking', 'tool', 'writing'])
const MAX_REASONING_CHARS = 140

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

export function normalizeSources(value: unknown): TutorStreamSource[] {
  if (!Array.isArray(value)) return []
  const out: TutorStreamSource[] = []
  for (const item of value) {
    if (!isRecord(item)) continue
    const title = str(item.title)
    if (!title) continue
    out.push({
      title,
      ...(str(item.id) ? { id: str(item.id) } : {}),
      ...(str(item.url) ? { url: str(item.url) } : {}),
      ...(str(item.kind) ? { kind: str(item.kind) } : {}),
    })
  }
  return out
}

/**
 * Map one parsed JSON payload (and the SSE `event:` name, if any) to a
 * normalised event. Returns null for frames that carry nothing to render
 * (unknown thinking payloads are deliberately dropped: raw chain-of-thought
 * must never reach the UI even if a misconfigured server forwards it).
 */
export function normalizeStreamEvent(
  payload: unknown,
  sseEventName?: string,
): TutorStreamEvent | null {
  if (!isRecord(payload)) return null
  const rawType =
    str(payload.type) ?? (sseEventName && sseEventName !== 'message' ? sseEventName : undefined)
  if (!rawType) return null
  const data = isRecord(payload.data) ? payload.data : {}

  switch (rawType) {
    case 'start':
    case 'connected':
      return { kind: 'start', sessionId: str(payload.sessionId) ?? str(data.sessionId) }
    case 'status': {
      const phase = str(data.phase)
      if (!phase || !PHASES.has(phase)) return null
      return {
        kind: 'status',
        phase: phase as TutorPhase,
        ...(str(data.tool) ? { tool: str(data.tool) } : {}),
        ...(str(data.label) ? { label: str(data.label) } : {}),
      }
    }
    case 'tool': {
      const name = str(data.name) ?? str(data.toolName)
      return name ? { kind: 'tool', name, label: str(data.label) } : null
    }
    case 'reasoning': {
      const text = str(data.text)
      return text ? { kind: 'reasoning', text: text.trim().slice(0, MAX_REASONING_CHARS) } : null
    }
    case 'content':
    case 'delta': {
      const text = typeof data.text === 'string' ? data.text : ''
      return text ? { kind: 'delta', text } : null
    }
    case 'sources':
      return { kind: 'sources', sources: normalizeSources(data.sources ?? payload.sources) }
    case 'end':
    case 'done':
    case 'final': {
      // Some servers nest the finish metadata under data.metadata.
      const meta = isRecord(data.metadata) ? data.metadata : data
      const sources = normalizeSources(meta.sources ?? data.sources)
      const stopReason = str(meta.stopReason ?? data.stopReason)
      const turnId = str(meta.turnId) ?? str(data.turnId)
      const stopped = meta.stopped === true || data.stopped === true || stopReason === 'stopped'
      return {
        kind: 'done',
        messageId: str(meta.messageId) ?? str(data.messageId),
        ...(sources.length ? { sources } : {}),
        ...(stopReason ? { stopReason } : {}),
        ...(stopped ? { stopped: true } : {}),
        ...(turnId ? { turnId } : {}),
      }
    }
    case 'error':
      return {
        kind: 'error',
        code: str(data.code),
        message: str(data.message) ?? 'The tutor is temporarily unavailable.',
      }
    case 'thinking':
      return null // internal only; never rendered
    default:
      return { kind: 'passthrough', type: rawType, data: payload.data }
  }
}
